import { ReactNode } from 'react';
import { Box, Tooltip, Badge, Text } from '@mantine/core';
import { IconSettings, IconArrowDown } from '@tabler/icons-react';
import { useEditMode } from '../../contexts/EditModeContext';
import { usePermissions } from '../../hooks/usePermissions';
import { PermissionPopover } from './PermissionPopover';
import { PermissionProvider, usePermissionContext, useInheritedPath } from './PermissionContext';
import type { CRUDPermission } from '../../api/permissionsApi';

interface PermissionControlledProps {
  /** Permission path - can be relative (auto-prefixed with parent) or absolute */
  path?: string;
  /** Explicit full path (ignores parent context) */
  absolutePath?: string;
  /** Whether to inherit permissions from parent (default: true) */
  inherit?: boolean;
  children: ReactNode;
  fallback?: ReactNode;
}

const OPS: { key: CRUDPermission; letter: string }[] = [
  { key: 'read', letter: 'R' },
  { key: 'create', letter: 'C' },
  { key: 'update', letter: 'U' },
  { key: 'delete', letter: 'D' },
];

export function PermissionControlled({ 
  path, 
  absolutePath,
  inherit = true, 
  children, 
  fallback = null 
}: PermissionControlledProps) {
  const { isEditMode, targetGroupId, activePopoverPath, openPopover, closePopover } = useEditMode();
  const { canRead, getPathPermissions, updateGroupPermissions } = usePermissions();
  const { inheritedPermissions, inheritedFrom } = usePermissionContext();
  
  // Resolve final path
  const resolvedPath = absolutePath || useInheritedPath(path);
  
  // Get own permissions for this path
  const ownPermissions = getPathPermissions(resolvedPath);
  
  // Determine effective permissions (own or inherited)
  const effectivePermissions = ownPermissions.length > 0 
    ? ownPermissions 
    : (inherit ? inheritedPermissions : []);
  
  const hasAccess = ownPermissions.includes('read') || 
    (inherit && inheritedPermissions.includes('read')) ||
    canRead(resolvedPath);
  
  const isInherited = ownPermissions.length === 0 && inherit && inheritedPermissions.length > 0;
  const isPopoverOpen = activePopoverPath === resolvedPath;

  // Not in edit mode - apply access control
  if (!isEditMode) {
    if (!hasAccess) {
      return <>{fallback}</>;
    }
    // Wrap children with PermissionProvider for inheritance
    return (
      <PermissionProvider path={resolvedPath} permissions={effectivePermissions}>
        {children}
      </PermissionProvider>
    );
  }

  // Edit mode - show with overlay and CRUD icons
  const handleSave = async (groupId: string, permissions: CRUDPermission[]) => {
    await updateGroupPermissions(groupId, resolvedPath, permissions);
  };

  // Build permission summary string
  const permSummary = OPS.filter(op => effectivePermissions.includes(op.key))
    .map(op => op.letter)
    .join('') || '—';

  return (
    <PermissionPopover
      path={resolvedPath}
      targetGroupId={targetGroupId}
      opened={isPopoverOpen}
      onClose={closePopover}
      onSave={handleSave}
    >
      <PermissionProvider path={resolvedPath} permissions={effectivePermissions}>
        <Box
          style={{
            position: 'relative',
            border: `2px dashed var(--mantine-color-${isInherited ? 'blue' : 'orange'}-4)`,
            borderRadius: 8,
            marginTop: 24,
            marginBottom: 8,
          }}
        >
          {children}
          
          {/* Permission Badge - compact indicator */}
          <Tooltip 
            label={
              <div>
                <Text fw={600} style={{ fontSize: 'var(--font-size-xs)' }}>{resolvedPath}</Text>
                {isInherited && inheritedFrom && (
                  <Text c="dimmed" style={{ fontSize: 'var(--font-size-xs)' }}>
                    <IconArrowDown size={10} style={{ verticalAlign: 'middle' }} /> от {inheritedFrom}
                  </Text>
                )}
              </div>
            } 
            withArrow 
            position="top"
          >
            <Badge
              size="sm"
              variant="filled"
              color={isInherited ? 'blue' : 'orange'}
              leftSection={isInherited ? <IconArrowDown size={12} /> : <IconSettings size={12} />}
              onClick={(e) => {
                e.stopPropagation();
                openPopover(resolvedPath);
              }}
              style={{
                position: 'absolute',
                top: -12,
                left: 8,
                cursor: 'pointer',
                zIndex: 100,
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
              }}
            >
              {permSummary}
            </Badge>
          </Tooltip>
        </Box>
      </PermissionProvider>
    </PermissionPopover>
  );
}

export default PermissionControlled;
